/**
 * Dashboard — partner home, v6 (two-zone working surface).
 *
 * Layout:
 *   • Single centered column (max-w 1200) — Toolbar · Hero · Alerts · KPI row ·
 *     chart · [Top offers | Activity] · Sources/Geo · NextBestAction
 *   • Newbie (zero conversions) — focused screen with only the "Старт" checklist
 *
 * No cinematic motion, no confetti, no duplicate intraday curves.
 * Dev state switcher only in import.meta.env.DEV.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Activity,
  AlertCircle,
  CircleCheck,
  Layers,
  Plus,
  Send,
  Sparkles,
  TrendingDown,
} from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  MiniSparkline,
  Pulse,
  Segmented,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
} from '@/components/ui';
import { AnomalyAlerts, NextBestAction, WelcomeChecklist } from '@/components/dashboard';
import { HeroBlock } from '@/components/dashboard/HeroBlock';
import { MainChart } from '@/components/dashboard/MainChart';
import { KpiRow } from '@/components/dashboard/KpiRow';
import { partnerApi } from '@/lib/api-affiliate';
import { formatCents, formatNumber } from '@/lib/format';
import type { Period, SeriesPoint } from '@/lib/types';
import {
  DASHBOARD_MOCKS,
  classifyState,
  type PartnerState,
  type DashboardMock,
  type ActivityMock,
} from '@/mocks/dashboard';

/** Тип события из ленты API → тип карточки активности дашборда. */
const ACTIVITY_TYPES: readonly string[] = ['conversion', 'click', 'payout', 'alert', 'system'];
function toActivityType(t: string): ActivityMock['type'] {
  return ACTIVITY_TYPES.includes(t) ? (t as ActivityMock['type']) : 'system';
}

// ─────────────────────────────────────────────────────────────────────────
//  Helpers
// ─────────────────────────────────────────────────────────────────────────

function relativeRu(iso: string): string {
  const t = new Date(iso).getTime();
  const diff = (Date.now() - t) / 1000;
  if (diff < 60) return 'только что';
  if (diff < 3600) return `${Math.floor(diff / 60)} мин назад`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} ч назад`;
  return new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: 'short' });
}

// ─────────────────────────────────────────────────────────────────────────
//  CountryChip — small typography chip for a country code (no flag emoji).
// ─────────────────────────────────────────────────────────────────────────

const COUNTRY_HUES = [
  { bg: 'color-mix(in oklch, var(--accent-bright) 14%, transparent)', fg: 'var(--accent-bright)' },
  { bg: 'color-mix(in oklch, var(--success) 14%, transparent)',       fg: 'var(--success)' },
  { bg: 'color-mix(in oklch, var(--warning) 14%, transparent)',       fg: 'var(--warning)' },
  { bg: 'color-mix(in oklch, var(--info) 14%, transparent)',          fg: 'var(--info)' },
  { bg: 'color-mix(in oklch, var(--danger) 14%, transparent)',        fg: 'var(--danger)' },
];

function hashCode(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function CountryChip({ code }: { code: string }) {
  const hue = COUNTRY_HUES[hashCode(code) % COUNTRY_HUES.length];
  return (
    <span
      className="inline-flex items-center justify-center align-middle h-6 min-w-[34px] px-1.5 rounded-[var(--radius-xs)] text-[10px] font-bold tabular-nums tracking-[0.06em]"
      style={{
        backgroundColor: hue.bg,
        color: hue.fg,
        border: `1px solid color-mix(in oklch, ${hue.fg} 26%, transparent)`,
      }}
      aria-label={`Страна: ${code}`}
    >
      {code}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Sources + Geo (compact two-column row, kept from v4 but simplified)
// ─────────────────────────────────────────────────────────────────────────

const SRC_COLORS = ['var(--accent-bright)', 'var(--success)', 'var(--warning)', 'var(--text-muted)'];

function SourcesAndGeo({ sources, geo }: { sources: DashboardMock['sources']; geo: DashboardMock['geo'] }) {
  if (sources.length <= 1 && geo.length === 0) return null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {sources.length > 1 && (
        <Card>
          <CardHeader>
            <CardTitle>
              <span className="inline-flex items-center gap-2">
                <Layers size={14} className="text-[var(--text-muted)]" />
                Источники трафика
              </span>
            </CardTitle>
            <span className="text-[11px] tabular-nums text-[var(--text-quiet)]">
              {formatNumber(sources.reduce((s, x) => s + x.count, 0))} кликов
            </span>
          </CardHeader>
          <CardContent className="!pt-4">
            <div className="flex h-2 rounded-full overflow-hidden mb-3 bg-[var(--bg-elevated)]">
              {sources.map((s, i) => (
                <div
                  key={s.label}
                  className="transition-all duration-700"
                  style={{ width: `${s.share * 100}%`, backgroundColor: SRC_COLORS[i % SRC_COLORS.length] }}
                  title={`${s.label}: ${(s.share * 100).toFixed(0)}%`}
                />
              ))}
            </div>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-2">
              {sources.map((s, i) => (
                <li key={s.label} className="flex items-center justify-between text-[12px] tabular-nums min-w-0">
                  <span className="flex items-center gap-1.5 min-w-0">
                    <span
                      className="inline-block w-2 h-2 rounded-full shrink-0"
                      style={{ backgroundColor: SRC_COLORS[i % SRC_COLORS.length] }}
                    />
                    <span className="truncate text-[var(--text-secondary)]">{s.label}</span>
                  </span>
                  <span className="text-[var(--text)] font-medium">
                    {(s.share * 100).toFixed(0)}<span className="text-[var(--text-quiet)] font-normal">%</span>
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
      {geo.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>
              <span className="inline-flex items-center gap-2">
                <Layers size={14} className="text-[var(--text-muted)]" />
                География
              </span>
            </CardTitle>
            <span className="text-[11px] tabular-nums text-[var(--text-quiet)]">
              {geo.reduce((s, g) => s + g.conversions, 0)} конв
            </span>
          </CardHeader>
          <CardContent className="!pt-4">
            <ul className="space-y-2">
              {geo.map((g) => (
                <li key={g.country} className="flex items-center gap-3">
                  <CountryChip code={g.country} />
                  <div className="flex-1 relative h-2 rounded-full overflow-hidden bg-[var(--bg-elevated)]">
                    <div
                      className="absolute inset-y-0 left-0 rounded-full"
                      style={{
                        width: `${g.share * 100}%`,
                        background: 'linear-gradient(90deg, var(--accent), var(--accent-bright))',
                        transition: 'width 0.9s var(--ease-out)',
                      }}
                    />
                  </div>
                  <span className="text-[11px] tabular-nums w-10 text-right text-[var(--text-muted)]">
                    {(g.share * 100).toFixed(0)}%
                  </span>
                  <span className="text-[12px] tabular-nums w-8 text-right text-[var(--text)] font-medium">
                    {g.conversions}
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  TopOffersTable — table primitives only, no inline markup.
// ─────────────────────────────────────────────────────────────────────────

function TopOffersTable({ offers }: { offers: DashboardMock['topOffers'] }) {
  if (offers.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Топ-офферы сегодня</CardTitle>
        </CardHeader>
        <CardContent>
          <EmptyState
            icon={<Sparkles className="h-7 w-7" />}
            title="Офферы ещё не сработали"
            description="Когда пойдут конверсии — здесь появится твой топ."
          />
        </CardContent>
      </Card>
    );
  }
  const totalCommission = offers.reduce((s, o) => s + o.commissionCents, 0);
  return (
    <Card className="overflow-hidden h-full flex flex-col">
      <CardHeader>
        <CardTitle>
          <span className="inline-flex items-center gap-2">
            Топ-офферы сегодня
            <span className="text-[11px] tabular-nums font-medium text-[var(--text-quiet)]">
              · {offers.length}
            </span>
          </span>
        </CardTitle>
        <a
          href="#/offers"
          className="text-[11px] uppercase tracking-[var(--tracking-eyebrow)] font-semibold text-[var(--text-muted)] hover:text-[var(--accent-bright)] transition-colors"
        >
          Все офферы →
        </a>
      </CardHeader>
      <Table>
        <THead>
          <TR className="hover:bg-transparent">
            <TH>Оффер</TH>
            <TH className="text-right w-[60px]">Гео</TH>
            <TH className="text-right w-[80px]">Тренд</TH>
            <TH className="text-right w-[70px]">Конв.</TH>
            <TH className="text-right w-[110px]">Доход</TH>
          </TR>
        </THead>
        <TBody>
          {offers.map((o, rowIdx) => {
            const isDrop = o.anomaly === 'drop';
            const first = o.trend[0] ?? 0;
            const last = o.trend[o.trend.length - 1] ?? 0;
            const dir = isDrop ? 'down' : last > first ? 'up' : last < first ? 'down' : 'flat';
            const sparkColor = dir === 'down' ? 'var(--danger)' : dir === 'up' ? 'var(--success)' : 'var(--text-muted)';
            const sharePct = totalCommission > 0 ? (o.commissionCents / totalCommission) * 100 : 0;
            return (
              <TR key={o.id}>
                <TD>
                  <a
                    href={`#/offers/${o.id}`}
                    className="flex items-center gap-2 min-w-0 hover:text-[var(--accent-bright)] transition-colors"
                  >
                    {isDrop && (
                      <span
                        className="block w-[3px] self-stretch rounded-full"
                        style={{ backgroundColor: 'var(--danger)' }}
                        aria-hidden
                      />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-medium text-[var(--text)] truncate flex items-center gap-2">
                        <span className="truncate">{o.name}</span>
                        {o.best && <Badge tone="success">Топ</Badge>}
                        {isDrop && (
                          <Badge tone="danger">
                            <TrendingDown size={10} />
                            Падение
                          </Badge>
                        )}
                      </div>
                      <div className="text-[11px] text-[var(--text-muted)] mt-0.5 tabular-nums">
                        {o.payoutLabel} · {sharePct.toFixed(0)}% дохода
                      </div>
                    </div>
                  </a>
                </TD>
                <TD className="text-right text-[var(--text-muted)]">
                  <div className="flex justify-end">
                    <CountryChip code={o.geo} />
                  </div>
                </TD>
                <TD className="text-right">
                  <div className="flex justify-end">
                    <MiniSparkline data={o.trend} width={64} height={22} color={sparkColor} delayMs={rowIdx * 80} />
                  </div>
                </TD>
                <TD className="text-right text-[var(--text)] font-medium">{o.conversions}</TD>
                <TD className="text-right font-bold text-[var(--text)]" style={{ letterSpacing: 'var(--tracking-tight)' }}>
                  {formatCents(o.commissionCents)}
                </TD>
              </TR>
            );
          })}
        </TBody>
      </Table>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Activity feed — filter chips + live pulse.
// ─────────────────────────────────────────────────────────────────────────

type ActivityFilter = 'all' | 'conversion' | 'click' | 'payout' | 'alert';

function ActivityFeed({ events }: { events: DashboardMock['activity'] }) {
  const [filter, setFilter] = useState<ActivityFilter>('all');
  const filtered = filter === 'all' ? events : events.filter((e) => e.type === filter);

  const ICON = {
    conversion: { Icon: CircleCheck, color: 'var(--success)' },
    click: { Icon: Activity, color: 'var(--accent-bright)' },
    payout: { Icon: Send, color: 'var(--accent)' },
    alert: { Icon: AlertCircle, color: 'var(--warning)' },
    system: { Icon: Sparkles, color: 'var(--text-muted)' },
  } as const;

  return (
    <Card className="h-full flex flex-col overflow-hidden">
      <CardHeader>
        <CardTitle>
          <span className="inline-flex items-center gap-2">
            Лента
            <span className="text-[11px] tabular-nums font-medium text-[var(--text-quiet)]">
              · {filtered.length}
            </span>
          </span>
        </CardTitle>
        <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[var(--tracking-eyebrow)] font-semibold text-[var(--success)]">
          <Pulse active color="var(--success)" />
          live
        </span>
      </CardHeader>
      <div className="px-5 py-3 flex gap-1.5 flex-wrap border-b border-[var(--border-subtle)] bg-[var(--bg-base)]/30">
        {(['all', 'conversion', 'click', 'payout', 'alert'] as ActivityFilter[]).map((f) => {
          const count = f === 'all' ? events.length : events.filter((e) => e.type === f).length;
          const active = filter === f;
          return (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={
                'inline-flex items-center gap-1 px-2 h-6 rounded-full text-[10px] uppercase tracking-[var(--tracking-eyebrow)] font-semibold transition-all duration-150 ' +
                (active
                  ? 'bg-[var(--accent-soft)] text-[var(--accent-bright)] border border-[color:var(--accent-soft)]'
                  : 'bg-transparent text-[var(--text-muted)] border border-[var(--border)] hover:text-[var(--text)] hover:border-[var(--border-strong)]')
              }
            >
              {{ all: 'Все', conversion: 'Конверсии', click: 'Клики', payout: 'Выплаты', alert: 'Алерты' }[f]}
              <span className={'tabular-nums normal-case ' + (active ? 'opacity-100' : 'opacity-60')}>
                {count}
              </span>
            </button>
          );
        })}
      </div>
      {filtered.length === 0 ? (
        <div className="px-5 pb-5 pt-3 text-[12px] text-[var(--text-muted)] text-center">
          Пусто. Конверсии и клики появятся здесь в реальном времени.
        </div>
      ) : (
        <ul className="flex-1 overflow-y-auto">
          {filtered.map((e) => {
            const { Icon, color } = ICON[e.type];
            const recent = Date.now() - new Date(e.iso).getTime() < 60_000;
            const isAmount = e.amountCents != null;
            const isPayout = e.type === 'payout';
            return (
              <li
                key={e.id}
                className="flex items-center gap-3 px-5 py-2.5 border-b border-[var(--border-subtle)] last:border-b-0 hover:bg-[color-mix(in_oklch,var(--accent-bright)_4%,transparent)] transition-colors"
              >
                <span
                  className="relative shrink-0 inline-flex h-7 w-7 items-center justify-center rounded-full"
                  style={{
                    backgroundColor: `color-mix(in oklch, ${color} 14%, transparent)`,
                    border: `1px solid color-mix(in oklch, ${color} 22%, transparent)`,
                  }}
                >
                  <Icon size={13} style={{ color }} strokeWidth={2.5} />
                  {recent && (
                    <span className="absolute -top-0.5 -right-0.5 inline-flex h-2 w-2" aria-hidden>
                      <Pulse active color="var(--success)" />
                    </span>
                  )}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] flex items-baseline justify-between gap-2 min-w-0">
                    <span className="truncate text-[var(--text)] font-medium">{e.title}</span>
                    {isAmount && (
                      <span
                        className="font-semibold tabular-nums shrink-0"
                        style={{ color: isPayout ? 'var(--accent-bright)' : 'var(--success)' }}
                      >
                        {isPayout ? '−' : '+'}{formatCents(e.amountCents!)}
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] mt-0.5 text-[var(--text-quiet)] tabular-nums">
                    {e.meta && <>{e.meta} <span className="opacity-50">·</span> </>}
                    {relativeRu(e.iso)}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Dev-only state switcher (only in import.meta.env.DEV)
// ─────────────────────────────────────────────────────────────────────────

function StateSwitcher({
  value,
  onChange,
}: {
  value: PartnerState | 'auto';
  onChange: (s: PartnerState | 'auto') => void;
}) {
  if (!import.meta.env.DEV) return null;
  return (
    <div
      className="fixed bottom-4 right-4 z-[100] rounded-[var(--radius-md)] border border-[var(--border-strong)] bg-[var(--bg-raised)] px-2 py-1.5 flex items-center gap-1 shadow-[var(--shadow-lg)]"
      role="group"
      aria-label="Dashboard state preview"
    >
      <span className="text-[10px] uppercase tracking-[var(--tracking-eyebrow)] px-2 text-[var(--text-quiet)] font-semibold">
        state
      </span>
      {(['auto', 'zero', 'warming', 'active', 'pro'] as const).map((s) => (
        <button
          key={s}
          type="button"
          onClick={() => onChange(s)}
          className={
            'px-2.5 py-1 text-[11px] font-medium rounded-[var(--radius-sm)] transition-colors ' +
            (value === s
              ? 'bg-[var(--accent)] text-[var(--accent-fg)]'
              : 'text-[var(--text-secondary)] hover:bg-[var(--bg-elevated)] hover:text-[var(--text)]')
          }
        >
          {s}
        </button>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Page
// ─────────────────────────────────────────────────────────────────────────

export function DashboardPage() {
  const me = partnerApi.me.useGet();
  const onboarding = partnerApi.onboarding.useStatus();
  const lifetime = partnerApi.lifetime.useGet();
  const [period, setPeriod] = useState<Period>('30d');
  // Реальные данные дашборда из API v2 (раньше всё рисовалось из DASHBOARD_MOCKS).
  const kpis = partnerApi.dashboard.useKpis(period);
  const series = partnerApi.dashboard.useSeries(period);
  const balance = partnerApi.balance.useGet();
  const activityQuery = partnerApi.activity.useList({ limit: 8 });
  const [stateOverride, setStateOverride] = useState<PartnerState | 'auto'>(() => {
    if (typeof window === 'undefined') return 'auto';
    const saved = window.localStorage.getItem('partner.dashboard.stateOverride.v4');
    if (saved === 'zero' || saved === 'warming' || saved === 'active' || saved === 'pro' || saved === 'auto') {
      return saved;
    }
    return 'auto';
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('partner.dashboard.stateOverride.v4', stateOverride);
  }, [stateOverride]);

  const autoState: PartnerState = useMemo(() => {
    const clicksAllTime = lifetime.data?.totalClicks ?? 0;
    const conversionsAllTime = lifetime.data?.totalConversions ?? 0;
    return classifyState({ clicksAllTime, conversionsAllTime, earnings30dCents: 0, conversions30d: 0 });
  }, [lifetime.data]);
  const partnerState: PartnerState = stateOverride === 'auto' ? autoState : stateOverride;

  // Сборка дашборда из реальных эндпоинтов. Блоки без бэкенда (топ-офферы,
  // источники, гео, тепловая карта) остаются пустыми — пустой scaffold из .zero,
  // компоненты для него показывают корректные empty-state.
  const real = useMemo<DashboardMock>(() => {
    const base = DASHBOARD_MOCKS.zero;
    const k = kpis.data;
    const items = series.data?.items ?? [];
    const tail = <T,>(sel: (it: (typeof items)[number]) => T): T[] => items.slice(-14).map(sel);
    const prevOf = (cur: number, pct: number | null | undefined): number => {
      if (pct == null) return 0; // у бэка не было предыдущего периода → previous = 0
      if (pct <= -100) return cur > 0 ? cur : 1; // -100%: previous был положительным (точное значение не восстановить)
      const p = Math.round(cur / (1 + pct / 100));
      return Number.isFinite(p) && p >= 0 ? p : 0;
    };
    const bal = balance.data;
    return {
      ...base,
      hero: {
        period: 'today',
        commissionCents: k?.commissionCentsValue ?? 0,
        commissionCentsPrev: prevOf(k?.commissionCentsValue ?? 0, k?.commissionDeltaPct),
        conversions: k?.conversionsValue ?? 0,
        clicks: k?.clicksValue ?? 0,
        sparkline: tail((it) => it.commissionCents),
      },
      kpis: {
        clicks: {
          current: k?.clicksValue ?? 0,
          previous: prevOf(k?.clicksValue ?? 0, k?.clicksDeltaPct),
          sparkline: tail((it) => it.clicks),
        },
        leads: {
          current: k?.leadsValue ?? 0,
          previous: prevOf(k?.leadsValue ?? 0, k?.leadsDeltaPct),
          sparkline: [],
        },
        conversions: {
          current: k?.conversionsValue ?? 0,
          previous: prevOf(k?.conversionsValue ?? 0, k?.conversionsDeltaPct),
          sparkline: tail((it) => it.conv),
        },
        epcCents: { current: k?.epc ?? 0, previous: k?.epc ?? 0, sparkline: [] },
      },
      chart: items.map(
        (it): SeriesPoint => ({
          date: it.date,
          clicks: it.clicks,
          leads: 0,
          conversions: it.conv,
          commissionCents: it.commissionCents,
        }),
      ),
      wallet: {
        availableCents: bal?.availableCents ?? 0,
        holdCents: bal?.holdCents ?? 0,
        pendingCents: bal?.pendingCents ?? 0,
        minPayoutCents: base.wallet.minPayoutCents,
      },
      activity: (activityQuery.data?.items ?? []).map((a) => ({
        id: a.id,
        type: toActivityType(a.type as string),
        title: a.title,
        meta: a.body ?? undefined,
        iso: a.createdAt,
      })),
    };
  }, [kpis.data, series.data, balance.data, activityQuery.data]);

  // В dev-режиме переключатель состояний показывает мок; иначе — реальные данные.
  const mock: DashboardMock =
    import.meta.env.DEV && stateOverride !== 'auto' ? DASHBOARD_MOCKS[partnerState] : real;

  const displayName =
    me.data?.displayName?.trim() || (me.data?.email ? me.data.email.split('@')[0] : 'partner');

  // Newbie who hasn't earned a single conversion yet → focused onboarding-only
  // screen: just the "Старт" checklist, nothing else. The DEV state switcher can
  // still force either view ('zero' → onboarding, 'warming/active/pro' → full).
  const noConversionYet = onboarding.data ? !onboarding.data.firstConversion : false;
  const showOnboardingOnly =
    stateOverride === 'auto' ? noConversionYet : stateOverride === 'zero';

  // Don't flash the full dashboard before the real onboarding status resolves.
  if (stateOverride === 'auto' && onboarding.isLoading && !onboarding.data) {
    return (
      <div className="grid place-items-center py-24 text-[var(--text-muted)]">
        <div className="h-5 w-5 rounded-full border-2 border-[var(--accent)] border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {showOnboardingOnly ? (
        /* Newbie, zero conversions — only the "Старт" checklist, centered */
        <div className="mx-auto w-full max-w-[880px]">
          <WelcomeChecklist />
        </div>
      ) : (
        /* Active layout — single centered column */
        <div className="mx-auto w-full max-w-[1200px] space-y-5">
          {/* Toolbar — period + primary CTA */}
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <Segmented<Period>
              value={period}
              onChange={setPeriod}
              options={[
                { value: '7d', label: '7д' },
                { value: '30d', label: '30д' },
                { value: '90d', label: '90д' },
                { value: '12m', label: 'Год' },
              ]}
            />
            <Button variant="gradient" size="sm" onClick={() => { window.location.hash = '/links?create=1'; }}>
              <Plus size={14} strokeWidth={2.5} />
              Создать ссылку
            </Button>
          </div>

          <HeroBlock
            state={partnerState}
            data={mock.hero}
            displayName={displayName}
            wallet={mock.wallet}
          />

          <AnomalyAlerts />

          <KpiRow kpis={mock.kpis} />

          <MainChart points={mock.chart} />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <TopOffersTable offers={mock.topOffers} />
            <ActivityFeed events={mock.activity} />
          </div>

          <SourcesAndGeo sources={mock.sources} geo={mock.geo} />

          <NextBestAction />
        </div>
      )}

      <StateSwitcher value={stateOverride} onChange={setStateOverride} />
    </div>
  );
}
